// BEAM UI primitive — Select (chip group).
// A horizontal, wrapping group of selectable option chips. Used where a native
// picker would be heavy (line type, event type, priority, tax rate, customer).
// Single-select. Consumes only theme tokens — no raw style values.
import React from 'react';
import { Pressable, StyleSheet, View, type StyleProp, type ViewStyle } from 'react-native';
import { theme } from '../../theme';
import { Text } from './Text';

export type SelectOption<T extends string> = { value: T; label: string };

type SelectProps<T extends string> = {
  label?: string;
  options: ReadonlyArray<SelectOption<T>>;
  value: T | null;
  onChange: (value: T) => void;
  style?: StyleProp<ViewStyle>;
};

export function Select<T extends string>({ label, options, value, onChange, style }: SelectProps<T>) {
  return (
    <View style={[styles.container, style]}>
      {label ? (
        <Text variant="small" color="secondary">
          {label}
        </Text>
      ) : null}
      <View style={styles.chips}>
        {options.map((opt) => {
          const selected = opt.value === value;
          return (
            <Pressable
              key={opt.value}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              accessibilityLabel={opt.label}
              onPress={() => onChange(opt.value)}
              style={({ pressed }) => [
                styles.chip,
                selected && styles.chipSelected,
                pressed && !selected && styles.chipPressed,
              ]}
            >
              <Text variant="small" color={selected ? 'onPrimary' : 'primary'}>
                {opt.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: { gap: theme.spacing.xs },
  chips: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
  chip: {
    minHeight: 36,
    paddingHorizontal: theme.spacing.md,
    paddingVertical: theme.spacing.sm,
    borderRadius: theme.radius.pill,
    borderWidth: theme.layout.hairline,
    borderColor: theme.color.border.strong,
    backgroundColor: theme.color.bg.surface,
    justifyContent: 'center',
  },
  chipSelected: {
    backgroundColor: theme.color.action.primary,
    borderColor: theme.color.action.primary,
  },
  chipPressed: { backgroundColor: theme.color.bg.subtle },
});
